/**
 * VoiceRecorder - Reusable voice recording component
 * Used in PreviewStage (Dialogue mode), LipsyncStage, and ActorPortal
 */
import { useState, useRef, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Mic, Square, Play, Pause, Trash2, Upload, Loader2 } from 'lucide-react';

export function VoiceRecorder({
  onRecordingComplete,
  onUpload,
  disabled = false,
  compact = false,
  showUploadOption = true,
  className = ""
}) {
  const [isRecording, setIsRecording] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordedUrl, setRecordedUrl] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const audioRef = useRef(null);
  const streamRef = useRef(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (recordedUrl) URL.revokeObjectURL(recordedUrl);
    };
  }, [recordedUrl]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setRecordedBlob(blob);
        const url = URL.createObjectURL(blob);
        setRecordedUrl(url);
        
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorderRef.current.start();
      setIsRecording(true);
      setRecordingTime(0);
      
      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
    } catch (error) {
      console.error('Failed to start recording:', error);
      alert('Could not access microphone. Please check permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  };

  const handlePlayPause = () => {
    if (!audioRef.current || !recordedUrl) return;
    
    if (isPlaying) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
    } else {
      audioRef.current.src = recordedUrl;
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const handleUseRecording = async () => {
    if (!recordedBlob) return;
    
    setUploading(true);
    try {
      await onRecordingComplete?.(recordedBlob);
      // Clear recording after successful upload
      clearRecording();
    } catch (error) {
      console.error('Failed to use recording:', error);
    } finally {
      setUploading(false);
    }
  };

  const clearRecording = () => {
    if (recordedUrl) URL.revokeObjectURL(recordedUrl);
    setRecordedBlob(null);
    setRecordedUrl(null);
    setRecordingTime(0);
    setIsPlaying(false);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('audio/')) {
      alert('Please select an audio file');
      return;
    }
    
    setUploading(true);
    try {
      await onUpload?.(file);
    } catch (error) {
      console.error('Upload failed:', error);
    } finally {
      setUploading(false);
      e.target.value = ''; // Reset input
    }
  };

  if (compact) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <audio ref={audioRef} className="hidden" onEnded={() => setIsPlaying(false)} />
        
        {!recordedBlob ? (
          <>
            <Button
              size="sm"
              variant={isRecording ? "destructive" : "outline"}
              onClick={isRecording ? stopRecording : startRecording}
              disabled={disabled || uploading}
              className="h-8"
            >
              {isRecording ? (
                <>
                  <Square className="w-3 h-3 mr-1" />
                  {formatTime(recordingTime)}
                </>
              ) : (
                <>
                  <Mic className="w-3 h-3 mr-1" />
                  Record
                </>
              )}
            </Button>
            
            {showUploadOption && !isRecording && (
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept="audio/*"
                  className="hidden"
                  onChange={handleFileUpload}
                  disabled={disabled || uploading}
                />
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8"
                  asChild
                  disabled={disabled || uploading}
                >
                  <span>
                    {uploading ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <>
                        <Upload className="w-3 h-3 mr-1" />
                        Upload
                      </>
                    )}
                  </span>
                </Button>
              </label>
            )}
          </>
        ) : (
          <>
            <Button
              size="sm"
              variant="outline"
              onClick={handlePlayPause}
              className="h-8"
            >
              {isPlaying ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
            </Button>
            <span className="text-xs text-zinc-400">{formatTime(recordingTime)}</span>
            <Button
              size="sm"
              variant="default"
              onClick={handleUseRecording}
              disabled={uploading}
              className="h-8 bg-green-600 hover:bg-green-700"
            >
              {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Use'}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={clearRecording}
              className="h-8 text-zinc-400 hover:text-red-400"
            >
              <Trash2 className="w-3 h-3" />
            </Button>
          </>
        )}
      </div>
    );
  }

  // Full-size variant
  return (
    <div className={`space-y-3 ${className}`}>
      <audio ref={audioRef} className="hidden" onEnded={() => setIsPlaying(false)} />
      
      {!recordedBlob ? (
        <div className="flex flex-col items-center gap-3">
          {/* Recording indicator */}
          <div className={`w-16 h-16 rounded-full flex items-center justify-center transition-all ${
            isRecording 
              ? 'bg-red-500 animate-pulse' 
              : 'bg-zinc-800 hover:bg-zinc-700'
          }`}>
            {isRecording ? (
              <div className="text-center">
                <div className="text-lg font-mono text-white">{formatTime(recordingTime)}</div>
              </div>
            ) : (
              <Mic className="w-6 h-6 text-zinc-400" />
            )}
          </div>
          
          <div className="flex gap-2">
            <Button
              size="sm"
              variant={isRecording ? "destructive" : "default"}
              onClick={isRecording ? stopRecording : startRecording}
              disabled={disabled || uploading}
            >
              {isRecording ? (
                <>
                  <Square className="w-4 h-4 mr-2" />
                  Stop
                </>
              ) : (
                <>
                  <Mic className="w-4 h-4 mr-2" />
                  Record Voice
                </>
              )}
            </Button>
            
            {showUploadOption && !isRecording && (
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept="audio/*"
                  className="hidden"
                  onChange={handleFileUpload}
                  disabled={disabled || uploading}
                />
                <Button
                  size="sm"
                  variant="outline"
                  asChild
                  disabled={disabled || uploading}
                >
                  <span>
                    {uploading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4 mr-2" />
                    )}
                    Upload File
                  </span>
                </Button>
              </label>
            )}
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-3">
          {/* Playback controls */}
          <div className="flex items-center gap-3 p-3 bg-zinc-800 rounded-lg">
            <Button
              size="sm"
              variant="ghost"
              onClick={handlePlayPause}
              className="w-10 h-10 rounded-full p-0"
            >
              {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
            </Button>
            <span className="text-sm text-zinc-300 font-mono min-w-[50px]">
              {formatTime(recordingTime)}
            </span>
            <Button
              size="sm"
              variant="ghost"
              onClick={clearRecording}
              className="text-zinc-400 hover:text-red-400"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
          
          <Button
            onClick={handleUseRecording}
            disabled={uploading}
            className="bg-green-600 hover:bg-green-700"
          >
            {uploading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Uploading...
              </>
            ) : (
              'Use This Recording'
            )}
          </Button>
        </div>
      )}
    </div>
  );
}

export default VoiceRecorder;
